import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";

/**
 * Sticky red banner shown when the current session is an impersonation session
 * (i.e. /api/auth/me returned `impersonated_by`). One-click end restores the
 * super-admin's original session on the backend and refreshes the auth context.
 */
export function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const isImpersonating = !!user?.impersonated_by;

  useEffect(() => {
    if (!isImpersonating) return;
    document.body.classList.add("impersonation-active");
    return () => document.body.classList.remove("impersonation-active");
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  const label = t("superadmin.impersonationBanner")
    .replace("{name}", user.name || user.email)
    .replace("{mins}", "60");

  const end = async () => {
    setBusy(true);
    try {
      await api.saEndImpersonation();
      await refresh();
      navigate("/superadmin");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-[60] bg-gradient-to-r from-[#F72585] via-[#D90429] to-[#F72585] text-white shadow-[0_4px_16px_rgba(217,4,41,0.4)]"
      data-testid="impersonation-banner"
    >
      <div className="mx-auto max-w-7xl px-4 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-lg">
            👤
          </span>
          <span className="font-bold text-sm truncate" data-testid="impersonation-label">{label}</span>
        </div>
        <button
          onClick={end}
          disabled={busy}
          className="rounded-full bg-white text-[#D90429] font-black px-4 py-1.5 text-sm hover:bg-white/90 disabled:opacity-50 transition"
          data-testid="end-impersonation-btn"
        >
          {t("superadmin.returnToSuperAdmin")}
        </button>
      </div>
    </div>
  );
}
